import { type ApolloCache, useApolloClient, useMutation } from '@provision/graphql-client';
import type { LegalDocumentKind } from '@provision/landing-page-utils';
import { useCallback } from 'react';
import {
  assignInList,
  saveInList,
} from 'src/graphql/entities/legal-document/legal-document-cache';
import {
  ASSIGN_LEGAL_DOCUMENT_MUTATION,
  GET_LEGAL_DOCUMENTS_QUERY,
  SAVE_LEGAL_DOCUMENT_MUTATION,
} from 'src/graphql/entities/legal-document/legal-document.operations';
import type {
  GetLegalDocumentsQueryData,
  LegalDocument,
  SaveLegalDocumentInput,
} from 'src/graphql/entities/legal-document/legal-document.types';

const queryFor = (partnerId: number | null) => ({
  query: GET_LEGAL_DOCUMENTS_QUERY,
  variables: { input: { partnerId: partnerId ?? 0 } },
});

const readDocuments = (cache: ApolloCache<unknown>, partnerId: number | null) =>
  cache.readQuery<GetLegalDocumentsQueryData>(queryFor(partnerId))?.getLegalDocuments ?? [];

const writeDocuments = (
  cache: ApolloCache<unknown>,
  partnerId: number | null,
  documents: LegalDocument[],
) =>
  cache.writeQuery<GetLegalDocumentsQueryData>({
    ...queryFor(partnerId),
    data: { getLegalDocuments: documents },
  });

const withTypename = (documents: LegalDocument[]) =>
  documents.map((document) => ({ __typename: 'LegalDocumentView', ...document }));

export const useManageLegalDocument = (partnerId: number | null) => {
  const client = useApolloClient();

  const [saveMutation, { loading: isSaving }] = useMutation<{
    saveLegalDocument: LegalDocument[];
  }>(SAVE_LEGAL_DOCUMENT_MUTATION);

  const [assignMutation] = useMutation<{ assignLegalDocument: LegalDocument[] }>(
    ASSIGN_LEGAL_DOCUMENT_MUTATION,
  );

  const saveDocument = useCallback(
    async (input: SaveLegalDocumentInput) => {
      await saveMutation({
        variables: { input: { partnerId, ...input } },
        optimisticResponse: {
          saveLegalDocument: withTypename(
            saveInList(
              readDocuments(client.cache, partnerId),
              input,
              new Date().toISOString(),
            ),
          ),
        },
        update: (cache, { data }) => {
          if (!data) return;

          writeDocuments(cache, partnerId, data.saveLegalDocument);
        },
      });
    },
    [saveMutation, client, partnerId],
  );

  // Applied at once, so an edit straight after builds on the new document, not the old one.
  const assignDocument = useCallback(
    async (brandId: string, kind: LegalDocumentKind, legalDocumentId: string) => {
      const current = readDocuments(client.cache, partnerId);

      await assignMutation({
        variables: { input: { partnerId, brandId, kind, legalDocumentId } },
        optimisticResponse: {
          assignLegalDocument: withTypename(
            assignInList(current, { brandId, kind, legalDocumentId }),
          ),
        },
        update: (cache, { data }) => {
          if (!data) return;

          writeDocuments(cache, partnerId, data.assignLegalDocument);
        },
      });
    },
    [assignMutation, client, partnerId],
  );

  return { saveDocument, assignDocument, isSaving };
};
